import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiX, FiHardDrive, FiArrowUp } from 'react-icons/fi';

export default function StorageWarningBanner() {
  const [storageData, setStorageData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStorage();
    // Re-check every 5 minutes
    const interval = setInterval(fetchStorage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchStorage() {
    try {
      const res = await fetch('/api/subscription', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStorageData(data);
      }
    } catch {
      // Silently fail, banner just won't show
    }
  }

  if (dismissed || !storageData) return null;

  const used = storageData.storage?.used || 0;
  const limit = storageData.storage?.limit;
  if (!limit) return null;

  const percent = Math.min(100, (used / limit) * 100);
  if (percent < 85) return null;

  const isEarlyAccess = storageData.tier === 'early_access' || storageData.tier === 'beta_vip';
  const isCritical = percent >= 95;
  const isFull = percent >= 100;

  function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  const displayPercent = percent >= 10 ? Math.round(percent) : Math.round(percent * 10) / 10;

  let message;
  if (isFull) {
    message = `Storage full (${formatBytes(used)} of ${formatBytes(limit)}). New uploads are blocked.`;
  } else if (isCritical) {
    message = `Storage almost full, ${displayPercent}% used (${formatBytes(used)} of ${formatBytes(limit)}). Uploads may fail soon.`;
  } else {
    message = `You're using ${displayPercent}% of your storage (${formatBytes(used)} of ${formatBytes(limit)}).`;
  }

  return (
    <div className={`storage-warning-banner ${isCritical || isFull ? 'critical' : 'warning'}`}>
      <div className="storage-warning-content">
        <FiAlertTriangle className="storage-warning-icon" />
        <span className="storage-warning-message">{message}</span>
        <div className="storage-warning-actions">
          {isEarlyAccess ? (
            <a href="mailto:jonny@dailey.llc" className="storage-warning-link">Contact support</a>
          ) : (
            <>
              <button onClick={() => navigate('/settings?tab=billing')} className="storage-warning-link">
                <FiArrowUp size={12} /> Upgrade
              </button>
              <button onClick={() => navigate('/settings?tab=billing&storage=add')} className="storage-warning-link">
                <FiHardDrive size={12} /> Add storage
              </button>
            </>
          )}
        </div>
      </div>
      <button className="storage-warning-dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">
        <FiX />
      </button>
    </div>
  );
}
